import { FACTS, SectionMeta, SectionStatus, type OutlineSection } from './prototype2Data';
import { outlineToReportSections } from './outlineToReportSections';
import { buildUserArtifactsContext, type UserArtifactsContext } from './buildUserArtifactsContext';
import type { ChatRequest } from '../_shared/chatService';
import type { ScreenId, UploadedFile } from './Prototype2Context';

const TRUNCATION_MARKER = '… [truncated]';

type ChatIntent = 'draft' | 'edit-current-draft' | 'expand' | 'qa' | 'propose-chart' | 'review';

const PARAGRAPH_BEGIN_MARKER = '<<<PARAGRAPH_BEGIN>>>';
const PARAGRAPH_END_MARKER = '<<<PARAGRAPH_END>>>';

const OUTLINE_OUTPUT_INSTRUCTIONS = `OUTLINE REVISION MODE (mandatory for this turn):

Return the COMPLETE updated report outline (all sections, not a diff) between these markers as JSON:

<<<OUTLINE_BEGIN>>>
[{"title":"1. Section Title","bullets":["bullet one","bullet two"]}]
<<<OUTLINE_END>>>

Rules:
- JSON must be a single array of objects with "title" (string, include section number) and "bullets" (string array).
- Include every section in the revised outline; preserve numbering (1., 2., …).
- Outside the markers, write at most 1–2 short sentences acknowledging the change (no markdown lists, no repeating the outline).
- Do not wrap the JSON in code fences.`;

type PanelState = 'chat-open' | 'chat-closed' | 'fullscreen-editor';

interface ResponseHint {
  shape: 'chat-reply' | 'draft-paragraph' | 'rewrite-paragraph' | 'outline-revision';
  max_words?: number;
}

interface ScreenContextSlice {
  intent: ChatIntent;
  screen_id?: ScreenId;
  active_section_id?: string;
  current_draft?: string;
  ui_state?: { panel?: PanelState; last_user_action?: string };
  response_hint?: ResponseHint;
}

const SCREEN_INDEX: Record<ScreenId, number> = {
  '01-define-narrative': 1,
  '02-review-outline': 2,
  '03-draft-report': 3,
};

const TOTAL_SCREENS = 3;
const MAX_CONTEXT_LENGTH = 32000;

interface ContextSection {
  id: string;
  title: string;
  index: number;
  status: SectionStatus;
  description: string;
  paragraphs: { id: string; num: number; title: string }[];
}

interface ActiveParagraphContext {
  paragraph_id: string;
  section_id: string;
  section_title: string;
  paragraph_num: number;
  paragraph_title: string;
  status: SectionStatus;
  current_draft?: string;
}

interface ResponseProtocol {
  surfaces: {
    chat_panel: string;
    paragraph_editor: string;
  };
  actions: {
    paragraph: { begin: string; end: string; notes: string };
  };
  rules: string[];
}

interface ContextObject {
  prototype: { id: 2; name: 'district-ai-tool' };
  screen: { id: ScreenId; index: number; total: number };
  intent: ChatIntent;
  district: typeof FACTS.district;
  report: {
    sections: ContextSection[];
    progress: { complete: number; total: number; percent: number };
  };
  active_paragraph?: ActiveParagraphContext;
  facts: typeof FACTS;
  ui_state: { panel?: PanelState; last_user_action?: string };
  response_hint?: ResponseHint;
  response_protocol: ResponseProtocol;
  outline_revision_instructions?: string;
  user_artifacts?: UserArtifactsContext;
  report_outline?: { sections: OutlineSection[] };
}

const PARAGRAPH_DRAFT_RESPONSE_PROTOCOL: ResponseProtocol = {
  surfaces: {
    chat_panel: 'Modify this section panel below the draft; conversational reply only when revising',
    paragraph_editor:
      'Draft paragraph field above; canonical EOAA district report paragraph prose copied verbatim into the UI',
  },
  actions: {
    paragraph: {
      begin: `${PARAGRAPH_BEGIN_MARKER} (or <<<PARAGRAPH_BEGIN:paragraph-id>>>)`,
      end: PARAGRAPH_END_MARKER,
      notes:
        'For edit-current-draft/expand: wrap the full replacement paragraph inside markers; keep acknowledgements outside. For intent=draft: output ONLY paragraph prose with no markers — the entire reply is pasted into the paragraph field.',
    },
  },
  rules: [
    'Write as published EOAA report prose. Use facts from context.facts; when specifics are missing, invent plausible district-level numbers without labeling them as placeholders or draft notes.',
    'Never include "Here is a draft", section/paragraph titles, "Draft notes", editorial commentary, or questions in paragraph output.',
    'For intent=draft: no markers, no preamble — only the final paragraph text.',
    'For intent=edit-current-draft: use PARAGRAPH markers to separate body from chat; inside markers is prose only.',
    'Never emit empty marker pairs.',
  ],
};

const findSectionForParagraph = (
  reportSections: SectionMeta[],
  paragraphId: string
): { section: SectionMeta; paragraphNum: number; paragraphTitle: string } | undefined => {
  for (const section of reportSections) {
    const para = section.paragraphs.find((p) => p.id === paragraphId);
    if (para) return { section, paragraphNum: para.num, paragraphTitle: para.title };
  }
  return undefined;
};

const truncateField = (value: string, allowance: number): string => {
  const cutoff = Math.max(100, allowance);
  return value.slice(0, cutoff) + TRUNCATION_MARKER;
};

const compactReportSections = (sections: ContextSection[]): ContextSection[] =>
  sections.map(({ id, title, index, status, paragraphs }) => ({
    id,
    title,
    index,
    status,
    description: '',
    paragraphs,
  }));

const buildCompactContext = (ctx: ContextObject, dropOutline: boolean): ContextObject => {
  const { report_outline: _outline, ...rest } = ctx;
  return {
    ...rest,
    report: { ...ctx.report, sections: compactReportSections(ctx.report.sections) },
    ...(dropOutline ? {} : { report_outline: ctx.report_outline }),
    facts: {
      district: ctx.facts.district,
      cohort_size: ctx.facts.cohort_size,
      postsecondary_readiness: ctx.facts.postsecondary_readiness,
      graduation: ctx.facts.graduation,
      college_eligibility: ctx.facts.college_eligibility,
      cte: ctx.facts.cte,
    },
  };
};

const serializeContext = (ctx: ContextObject): string => {
  const attempts: ContextObject[] = [ctx, buildCompactContext(ctx, false), buildCompactContext(ctx, true)];

  for (const candidate of attempts) {
    const serialized = JSON.stringify(candidate);
    if (serialized.length <= MAX_CONTEXT_LENGTH) return serialized;
  }

  let working = buildCompactContext(ctx, true);

  if (working.active_paragraph?.current_draft) {
    const serializedBefore = JSON.stringify(working);
    const allowance =
      MAX_CONTEXT_LENGTH - (serializedBefore.length - working.active_paragraph.current_draft.length) - 200;
    working = {
      ...working,
      active_paragraph: {
        ...working.active_paragraph,
        current_draft: truncateField(working.active_paragraph.current_draft, allowance),
      },
    };
  }

  if (working.user_artifacts?.additional_context) {
    const serializedBefore = JSON.stringify(working);
    const allowance =
      MAX_CONTEXT_LENGTH - (serializedBefore.length - working.user_artifacts.additional_context.length) - 200;
    working = {
      ...working,
      user_artifacts: {
        ...working.user_artifacts,
        additional_context: truncateField(working.user_artifacts.additional_context, allowance),
      },
    };
  }

  let serialized = JSON.stringify(working);
  if (serialized.length <= MAX_CONTEXT_LENGTH) return serialized;

  const essential: ContextObject = {
    prototype: working.prototype,
    screen: working.screen,
    intent: working.intent,
    district: working.district,
    report: {
      sections: compactReportSections(working.report.sections).slice(0, 4),
      progress: working.report.progress,
    },
    active_paragraph: working.active_paragraph,
    facts: working.facts,
    ui_state: working.ui_state,
    response_hint: working.response_hint,
    response_protocol: working.response_protocol,
    ...(working.outline_revision_instructions
      ? { outline_revision_instructions: working.outline_revision_instructions }
      : {}),
    ...(working.user_artifacts ? { user_artifacts: working.user_artifacts } : {}),
  };

  serialized = JSON.stringify(essential);
  if (serialized.length > MAX_CONTEXT_LENGTH) {
    serialized = serialized.slice(0, MAX_CONTEXT_LENGTH - TRUNCATION_MARKER.length) + TRUNCATION_MARKER;
  }
  return serialized;
};

interface BuildChatRequestArgs {
  message: string;
  screenId: ScreenId;
  slice: ScreenContextSlice;
  conversationId?: string;
  scope: string;
  sectionStatuses: Record<string, SectionStatus>;
  uploadedFiles: UploadedFile[];
  contextText: string;
  outlineSections: OutlineSection[];
  reportSections: SectionMeta[];
  eoaId: string;
}

const buildChatRequest = ({
  message,
  screenId,
  slice,
  conversationId,
  scope,
  sectionStatuses,
  uploadedFiles,
  contextText,
  outlineSections,
  reportSections,
  eoaId,
}: BuildChatRequestArgs): ChatRequest => {
  const isOutlineRevision = slice.response_hint?.shape === 'outline-revision';
  const effectiveReportSections = reportSections.length > 0 ? reportSections : outlineToReportSections(outlineSections);
  const completeCount = effectiveReportSections.filter(
    (s) => (sectionStatuses[s.id] ?? s.status) === 'complete'
  ).length;
  const total = effectiveReportSections.length;

  const isInitialParagraphDraft = slice.intent === 'draft';
  const isParagraphRevision = slice.intent === 'edit-current-draft' || slice.intent === 'expand';
  const activeMatch = slice.active_section_id
    ? findSectionForParagraph(effectiveReportSections, slice.active_section_id)
    : undefined;
  const activeParagraph: ActiveParagraphContext | undefined = activeMatch
    ? {
        paragraph_id: slice.active_section_id as string,
        section_id: activeMatch.section.id,
        section_title: activeMatch.section.title,
        paragraph_num: activeMatch.paragraphNum,
        paragraph_title: activeMatch.paragraphTitle,
        status: sectionStatuses[activeMatch.section.id] ?? activeMatch.section.status,
        current_draft: slice.current_draft,
      }
    : undefined;

  const userArtifacts = buildUserArtifactsContext(uploadedFiles, contextText);

  const responseProtocol: ResponseProtocol = isOutlineRevision
    ? {
        surfaces: {
          chat_panel: 'outline review chat; short acknowledgement only',
          paragraph_editor: 'not used during outline revision',
        },
        actions: {
          paragraph: {
            begin: PARAGRAPH_BEGIN_MARKER,
            end: PARAGRAPH_END_MARKER,
            notes: 'Do not emit paragraph markers during outline revision.',
          },
        },
        rules: [
          'Return the revised outline between <<<OUTLINE_BEGIN>>> and <<<OUTLINE_END>>> markers.',
          'Keep conversational text outside markers to at most 1–2 short sentences.',
        ],
      }
    : isInitialParagraphDraft || isParagraphRevision
      ? PARAGRAPH_DRAFT_RESPONSE_PROTOCOL
      : {
          surfaces: {
            chat_panel: 'narrative/outline chat',
            paragraph_editor: 'not active on this screen',
          },
          actions: {
            paragraph: {
              begin: PARAGRAPH_BEGIN_MARKER,
              end: PARAGRAPH_END_MARKER,
              notes: 'Do not emit paragraph markers unless drafting report content.',
            },
          },
          rules: ['Reply conversationally unless the user requests report content.'],
        };

  const contextObj: ContextObject = {
    prototype: { id: 2, name: 'district-ai-tool' },
    screen: { id: screenId, index: SCREEN_INDEX[screenId], total: TOTAL_SCREENS },
    intent: slice.intent,
    district: FACTS.district,
    report: {
      sections: effectiveReportSections.map(({ id, title, index, status, description, paragraphs }) => ({
        id,
        title,
        index,
        status: sectionStatuses[id] ?? status,
        description,
        paragraphs: paragraphs.map((p) => ({ id: p.id, num: p.num, title: p.title })),
      })),
      progress: {
        complete: completeCount,
        total,
        percent: Math.round((completeCount / total) * 100),
      },
    },
    active_paragraph: activeParagraph,
    facts: FACTS,
    ui_state: slice.ui_state ?? {},
    response_hint: slice.response_hint,
    response_protocol: responseProtocol,
    ...(isOutlineRevision ? { outline_revision_instructions: OUTLINE_OUTPUT_INSTRUCTIONS } : {}),
    ...(userArtifacts ? { user_artifacts: userArtifacts } : {}),
    report_outline: { sections: outlineSections },
  };

  return {
    prompt: message,
    eoa_id: eoaId,
    scope,
    context: serializeContext(contextObj),
    conversation_id: conversationId,
    mode: 'mock',
  };
};

export { buildChatRequest };
export type { ChatIntent, ScreenContextSlice, ResponseHint, PanelState };
